import { cn } from "@/lib/utils";
import { ui } from "@patient-watch/ui";
import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { AppSearchInput } from "./AppSearchInput";

/** Square toolbar icons, matching `AppSearchInput` (`h-12`). */
export const APP_LIST_TOOLBAR_ICON_CLASS = "size-12 !h-12 !w-12 shrink-0 p-0";

interface AppListToolbarSearch {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    "aria-label": string;
    clearAriaLabel?: string;
}

interface AppListToolbarProps {
    search: AppListToolbarSearch;
    filters?: ReactNode;
    desktopActions?: ReactNode;
    mobileAction?: ReactNode;
    className?: string;
}

/** Search, filters, and the mobile create action used on Team, Studies, and Validate. */
export function AppListToolbar({ search, filters, desktopActions, mobileAction, className }: AppListToolbarProps) {
    return (
        <div className={cn("flex items-center gap-2 w-full min-w-0", className)}>
            <AppSearchInput
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
                aria-label={search["aria-label"]}
                clearAriaLabel={search.clearAriaLabel}
            />
            {filters}
            {desktopActions}
            {mobileAction}
        </div>
    );
}

interface AppListActionButtonProps {
    label: string;
    onClick: () => void;
    layout: "desktop" | "mobile";
    "aria-label"?: string;
    "data-testid"?: string;
}

/** Desktop header CTA, or the matching icon button that sits in `AppListToolbar` on mobile. */
export function AppListActionButton({
    label,
    onClick,
    layout,
    "aria-label": ariaLabel,
    "data-testid": testId,
}: AppListActionButtonProps) {
    if (layout === "mobile") {
        return (
            <ui.Button
                type="button"
                size="icon"
                className={cn("md:hidden", APP_LIST_TOOLBAR_ICON_CLASS)}
                data-testid={testId}
                aria-label={ariaLabel ?? label}
                onClick={onClick}
            >
                <Plus size={20} strokeWidth={2} aria-hidden />
            </ui.Button>
        );
    }

    return (
        <ui.Button
            type="button"
            size="sm"
            className="gap-2"
            data-testid={testId}
            aria-label={ariaLabel ?? label}
            onClick={onClick}
        >
            <Plus size={20} strokeWidth={2} aria-hidden />
            <span>{label}</span>
        </ui.Button>
    );
}
